import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { exportOrdersToPDF, exportOrdersToExcel } from "../../helper/exportHelpers";
import { API_BASE } from "../../utils/api";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Menu } from "primereact/menu";
import { formatCurrency } from "../../utils/currency";
import "./DownloadOrdersPage.css";

export default function DownloadOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [phoneNumberFilter, setPhoneNumberFilter] = useState("");
  const [addressFilter, setAddressFilter] = useState("");
  const [productsFilter, setProductsFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [timeTakenFilter, setTimeTakenFilter] = useState("");
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const navigate = useNavigate();

  const menu = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      const [ordersRes, historyRes] = await Promise.all([
        axios.get(`${API_BASE}/api/orders/allOrderDetails`),
        axios.get(`${API_BASE}/api/orders/statusHistoryForAll`)
      ]);
      const orders = ordersRes.data;
      const allHistory = historyRes.data;

      const historyMap = {};
      allHistory.forEach((entry) => {
        const id = entry.orderId;
        if (!historyMap[id]) historyMap[id] = [];
        historyMap[id].push(entry);
      });

      const ordersWithTime = orders.map((order) => {
        const history = historyMap[order._id] || [];
        const firstStatus = history[0]?.createdAt;
        const completedStatus = history.find(h => h.status === "completed")?.createdAt;
        let timeTaken = "Not Completed";
        if (firstStatus && completedStatus) {
          const start = new Date(firstStatus);
          const end = new Date(completedStatus);
          const diffMs = end - start;
          const totalSeconds = Math.floor(diffMs / 1000);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          timeTaken = `${hours}h ${minutes}m ${seconds}s`;
        }
        const productTitlesString = order.products
          .map(p => p.productTitle)
          .join(", ")
          .toLowerCase();
        return { ...order, timeTaken, productTitlesString };
      });
      setOrders(ordersWithTime);
      setUniqueStatuses([...new Set(ordersWithTime.map(o => o.status))]);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...orders];
    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter(order => new Date(order.createdAt) >= from);
    }
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => new Date(order.createdAt) <= to);
    }
    if (orderIdFilter) {
      filtered = filtered.filter(order =>
        (order.orderId || "").toLowerCase().includes(orderIdFilter.toLowerCase())
      );
    }
    if (phoneNumberFilter) {
      filtered = filtered.filter(order =>
        (order.phoneNumber || "").toLowerCase().includes(phoneNumberFilter.toLowerCase())
      );
    }
    if (addressFilter) {
      filtered = filtered.filter(order =>
        (order.address || "").toLowerCase().includes(addressFilter.toLowerCase())
      );
    }
    if (productsFilter) {
      filtered = filtered.filter(order =>
        (order.productTitlesString || "").includes(productsFilter.toLowerCase())
      );
    }
    if (amountFilter) {
      filtered = filtered.filter(order =>
        String(order.amount).toLowerCase().includes(amountFilter.toLowerCase())
      );
    }
    if (driverFilter) {
      filtered = filtered.filter(order =>
        ((order.assignedDriver?.name) || "Not Assigned").toLowerCase().includes(driverFilter.toLowerCase())
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    if (timeTakenFilter) {
      filtered = filtered.filter(order =>
        (order.timeTaken || "").toLowerCase().includes(timeTakenFilter.toLowerCase())
      );
    }
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [
    orders,
    fromDate,
    toDate,
    orderIdFilter,
    phoneNumberFilter,
    addressFilter,
    productsFilter,
    amountFilter,
    driverFilter,
    statusFilter,
    timeTakenFilter
  ]);

  const handleClearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setOrderIdFilter("");
    setPhoneNumberFilter("");
    setAddressFilter("");
    setProductsFilter("");
    setAmountFilter("");
    setDriverFilter("");
    setStatusFilter("");
    setTimeTakenFilter("");
  };

  const downloadItems = [
    {
      label: "Export as PDF",
      icon: "pi pi-file-pdf",
      command: () => {
        if (!filteredOrders.length) {
          window.alert("No data to download.");
          return;
        }
        exportOrdersToPDF(filteredOrders);
      }
    },
    {
      label: "Export as Excel",
      icon: "pi pi-file-excel",
      command: () => {
        if (!filteredOrders.length) {
          window.alert("No data to download.");
          return;
        }
        exportOrdersToExcel(filteredOrders);
      }
    }
  ];

  // Pagination logic for PrimeReact
  const totalRecords = filteredOrders.length;
  const first = (currentPage - 1) * rowsPerPage;
  const paginatedOrders = filteredOrders.slice(first, first + rowsPerPage);

  const onPageChange = (event) => {
    setCurrentPage(Math.floor(event.first / event.rows) + 1);
  };

  return (
    <div className="download-orders-container">
      <Button
        label="Back"
        icon="pi pi-arrow-left"
        className="p-button-secondary mb-3"
        style={{ minWidth: 60, marginBottom: 12 }}
        onClick={() => navigate(-1)}
      />

      <div className="prime-filters-row">
        <div>
         
          <Calendar
            value={fromDate}
            onChange={e => setFromDate(e.value)}
            dateFormat="yy-mm-dd"
            placeholder="From"
            showIcon
            className="prime-calendar"
          />
        </div>
        <div>
          
          <Calendar
            value={toDate}
            onChange={e => setToDate(e.value)}
            dateFormat="yy-mm-dd"
            placeholder="To"
            showIcon
            className="prime-calendar"
          />
        </div>
        <Button
          label="Clear"
          icon="pi pi-times"
          className="p-button-secondary prime-filter-btn"
          onClick={handleClearFilters}
        />
        {/* <Button
          label="Filter"
          icon="pi pi-filter"
          className="p-button-info prime-filter-btn"
          onClick={() => {}}
        /> */}
        <Button
          label="Download"
          icon="pi pi-download"
          className="p-button-success prime-filter-btn"
          onClick={e => menu.current.toggle(e)}
          aria-haspopup
          aria-controls="download_menu"
        />
        <Menu model={downloadItems} popup ref={menu} id="download_menu" />
      </div>

      <div className="table-scroll-outer">
        <div className="table-scroll-inner">
          <Table className="custom-fit-table" bordered hover>
            <thead>
              <tr>
                <th style={{width: "130px"}}>Order ID</th>
                <th style={{width: "110px"}}>Phone</th>
                <th style={{width: "160px"}}>Address</th>
                <th style={{width: "260px"}}>Products</th>
                <th style={{width: "90px"}}>Amount</th>
                <th style={{width: "105px"}}>Time Taken</th>
                <th style={{width: "120px"}}>Driver</th>
                <th style={{width: "115px"}}>Status</th>
              </tr>
              <tr className="table-filter-row">
                <th>
                  <input
                    type="text"
                    value={orderIdFilter}
                    onChange={e => setOrderIdFilter(e.target.value)}
                    placeholder="Filter"
                    className="prime-column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    value={phoneNumberFilter}
                    onChange={e => setPhoneNumberFilter(e.target.value)}
                    placeholder="Filter"
                    className="prime-column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    value={addressFilter}
                    onChange={e => setAddressFilter(e.target.value)}
                    placeholder="Filter"
                    className="prime-column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    value={productsFilter}
                    onChange={e => setProductsFilter(e.target.value)}
                    placeholder="Filter"
                    className="prime-column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    value={amountFilter}
                    onChange={e => setAmountFilter(e.target.value)}
                    placeholder="Filter"
                    className="prime-column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    value={timeTakenFilter}
                    onChange={e => setTimeTakenFilter(e.target.value)}
                    placeholder="Filter"
                    className="prime-column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    value={driverFilter}
                    onChange={e => setDriverFilter(e.target.value)}
                    placeholder="Filter"
                    className="prime-column-filter"
                  />
                </th>
                <th>
                  <select
                    className="prime-column-filter"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    {uniqueStatuses.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center">No data found</td>
                </tr>
              ) : (
                paginatedOrders.map((order, idx) => (
                  <tr key={idx}>
                    <td>{order.orderId}</td>
                    <td>{order.phoneNumber}</td>
                    <td>{order.address}</td>
                    <td>
                      <ul className="table-products-list">
                        {order.products.map((p, i) =>
                          <li key={i}>{p.productTitle} × {p.quantity}</li>
                        )}
                      </ul>
                    </td>
                    <td>{formatCurrency(order.amount)}</td>
                    <td>{order.timeTaken}</td>
                    <td>{order.assignedDriver?.name || "Not Assigned"}</td>
                    <td>{order.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {/* Paginator inside scrollable area */}
          <div className="table-pagination-center">
            <Paginator
              first={first}
              rows={rowsPerPage}
              totalRecords={totalRecords}
              onPageChange={onPageChange}
              template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
              alwaysShow={true}
              disabled={totalRecords === 0}
            />
          </div>
        </div>
      </div>
    </div>
  );
}